import React from "react";
import {
    Chip,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { PILL_FIELD_SX } from "./batchAssessmentColors";

const TERM_OPTIONS = [1, 2, 3];

const ACADEMIC_YEAR_OPTIONS = [
    "2025-2026",
    "2026-2027",
    "2027-2028",
    "2028-2029",
    "2029-2030",
];

const BatchAssessmentFilters = ({
    subjects = [],
    units = [],

    selectedSubject,
    selectedUnit,
    selectedTerm,

    classLevel,
    sectionLevel,
    academicYear,

    searchTerm,

    onSubjectChange,
    onUnitChange,
    onTermChange,

    onClassLevelChange,
    onSectionLevelChange,
    onAcademicYearChange,

    onSearchChange,

    resultCount = null,
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: {
                    xs: 2,
                    sm: 2.5,
                },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                >
                    1. Choose What You’re Assessing
                </Typography>

                {resultCount !== null && (
                    <Chip
                        size="small"
                        label={
                            `${resultCount} student${
                                resultCount === 1 ? "" : "s"
                            } found`
                        }
                        sx={{
                            fontWeight: 700,
                            color: "#425126",
                            backgroundColor: "#EEF3E4",
                            border: "1px solid",
                            borderColor: "#CFDBB4",
                        }}
                    />
                )}
            </Stack>

            <Grid
                container
                spacing={1.5}
            >
                {/* Subject */}
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Subject"
                        value={selectedSubject}
                        onChange={(event) =>
                            onSubjectChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    >
                        {subjects.map((subject) => (
                            <MenuItem
                                key={subject.id}
                                value={subject.id}
                            >
                                {subject.subject_name}
                                {subject.class_level
                                    ? ` - Class ${subject.class_level}`
                                    : ""}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Unit */}
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Unit"
                        value={selectedUnit}
                        disabled={!selectedSubject}
                        onChange={(event) =>
                            onUnitChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    >
                        {units.map((unit) => (
                            <MenuItem
                                key={unit.id}
                                value={unit.id}
                            >
                                {unit.unit_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Term */}
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={2}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Term"
                        value={selectedTerm}
                        onChange={(event) =>
                            onTermChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    >
                        {TERM_OPTIONS.map((term) => (
                            <MenuItem
                                key={term}
                                value={term}
                            >
                                Term {term}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Search */}
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                >
                    <TextField
                        fullWidth
                        size="small"
                        label="Search students"
                        placeholder="Search by student name..."
                        value={searchTerm}
                        onChange={(event) =>
                            onSearchChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    />
                </Grid>

                {/* Class */}
                <Grid
                    item
                    xs={12}
                    sm={4}
                    md={4}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Class"
                        value={classLevel}
                        onChange={(event) =>
                            onClassLevelChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    >
                        <MenuItem value="">
                            All Classes
                        </MenuItem>

                        {[
                            "KG",
                            1,
                            2,
                            3,
                            4,
                            5,
                            6,
                            7,
                            8,
                        ].map((level) => (
                            <MenuItem
                                key={level}
                                value={level}
                            >
                                Class {level}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Section */}
                <Grid
                    item
                    xs={12}
                    sm={4}
                    md={4}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Section"
                        value={sectionLevel}
                        onChange={(event) =>
                            onSectionLevelChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    >
                        <MenuItem value="">
                            All Sections
                        </MenuItem>

                        {[1, 2, 3].map((section) => (
                            <MenuItem
                                key={section}
                                value={section}
                            >
                                Section {section}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Academic Year */}
                <Grid
                    item
                    xs={12}
                    sm={4}
                    md={4}
                >
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Academic Year"
                        value={academicYear}
                        onChange={(event) =>
                            onAcademicYearChange(
                                event.target.value
                            )
                        }
                        sx={PILL_FIELD_SX}
                    >
                        <MenuItem value="">
                            All Academic Years
                        </MenuItem>

                        {ACADEMIC_YEAR_OPTIONS.map(
                            (year) => (
                                <MenuItem
                                    key={year}
                                    value={year}
                                >
                                    {year}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default BatchAssessmentFilters;